// Settings → Team: gross-margin colour thresholds and per-trade green targets
// (COST-01). Seen with costing.view; edited with costing.manage.
import { useEffect, useState } from 'react'
import { useApiData } from '@/hooks/useApi'
import { useCostingPermission } from '@/components/JobCosting/fixturePermissions'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { toast } from '@/lib/toast'
import { costingApi, designMode, type CostingSettings } from '@/components/JobCosting/api'

export function MarginTargetsSection() {
  const canView = useCostingPermission('costing.view')
  if (!canView) return null
  return <MarginTargetsInner key={String(designMode())} />
}

const label = (k: string) => k.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/^Tv\b/, 'TV')

function MarginTargetsInner() {
  const demo = designMode()
  const canManage = useCostingPermission('costing.manage')
  const q = useApiData<CostingSettings>(() => costingApi.settings(), [])
  const [red, setRed] = useState('')
  const [green, setGreen] = useState('')
  const [trades, setTrades] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!q.data) return
    setRed(String(q.data.redBelow)); setGreen(String(q.data.greenAt))
    setTrades(Object.fromEntries(Object.entries(q.data.tradeTargets).map(([k, v]) => [k, String(v)])))
  }, [q.data])

  async function save() {
    const r = Number(red), g = Number(green)
    const t = Object.fromEntries(Object.entries(trades).map(([k, v]) => [k, Number(v)]))
    const all = [r, g, ...Object.values(t)]
    if (all.some((n) => !Number.isFinite(n) || n < -100 || n > 100)) { setError('Enter percentages between -100 and 100.'); return }
    if (r > g) { setError('Red must be at or below the green line.'); return }
    setError(undefined); setSaving(true)
    try { await costingApi.updateSettings({ redBelow: r, greenAt: g, tradeTargets: t }); toast.success(demo ? 'Demo targets updated temporarily' : 'Margin targets saved'); q.refetch() }
    catch (err) { toast.error(err, 'save the margin targets') }
    finally { setSaving(false) }
  }

  const s = q.data
  return (
    <section aria-label="Margin targets" className="mt-6">
      <h3 className="text-base font-semibold">Margin targets</h3>
      {demo && <p>Demo — targets are temporary.</p>}
      <p className="text-xs" style={{ color: 'var(--midnight-text3)', marginBottom: 8 }}>
        Gross margin colours: red below the red line, amber up to the green line, green at or above it. A trade target replaces the green line for that trade.
      </p>
      {q.error ? <p role="alert" className="text-sm">{q.error}</p> : !s ? <div className="h-10 shimmer rounded" /> : canManage ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input label="Red below (%)" inputMode="decimal" value={red} onChange={(e) => setRed(e.target.value)} />
            <Input label="Green at (%)" inputMode="decimal" value={green} onChange={(e) => setGreen(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {Object.keys(trades).map((k) => (
              <Input key={k} label={`${label(k)} target (%)`} inputMode="decimal" value={trades[k]} onChange={(e) => setTrades({ ...trades, [k]: e.target.value })} />
            ))}
          </div>
          {error && <p role="alert" className="text-sm" style={{ color: 'var(--blush-text)' }}>{error}</p>}
          <div><Button size="sm" variant="secondary" needsNetwork loading={saving} onClick={save}>Save targets</Button></div>
        </div>
      ) : (
        <div className="text-sm">
          <p>Red below {s.redBelow}% · green at {s.greenAt}%</p>
          <p style={{ color: 'var(--midnight-text3)' }}>{Object.entries(s.tradeTargets).map(([k, v]) => `${label(k)} ${v}%`).join(' · ')}</p>
        </div>
      )}
    </section>
  )
}
